// src/cockpit4/components/HermesTaskExecutor.tsx
// ============================================
// Hermes 助手 P0-功能2：任务执行能力
//
// 功能定位：
//   Hermes 不只是聊天，能真正做事——通过自然语言指令驱动跨模块执行。
//   1. 自然语言指令执行（"帮我生成一篇公众号文章"、"查今日收入"等）
//   2. 跨模块调用（营销/媒体/CRM/财务等模块 Runtime）
//   3. 执行过程反馈和进度展示（分步状态 + 进度条）
//   4. 任务历史和可追溯（localStorage 持久化）
//   5. 复杂任务分解和分步执行（按任务类型生成步骤计划）
//   6. 执行结果确认和修改（结果预览 + 重试）
//
// 数据来源（禁止假数据）：
//   - 任务执行记录：localStorage（key: xeaos_hermes_tasks）持久化
//   - 跨模块调用：RuntimeManager.query 对应 Runtime
//       · 内容生成类 → MediaStudioRuntime（list_packages）
//       · 数据查询-收入 → RevenueEngineRuntime（today_revenue）
//       · 数据查询-客户 → CRMRuntime（stats）
//       · 数据查询-项目 → 本地任务（xeaos_tasks_list，无 ProjectRuntime 时降级）
//       · 任务管理类 → SchedulerRuntime（list）
//       · 报告生成类 → HermesRuntime（status）
//   - 无数据时显示"暂无执行任务 / 输入指令开始"
//
// 指令识别规则引擎（INSTRUCTION_RULES）：
//   按关键词命中识别任务类型与目标 Runtime，见下方常量定义。
//   未命中时默认归为 report_generation（交由 Hermes 兜底）。
// ============================================

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  Send,
  Loader2,
  CheckCircle2,
  AlertOctagon,
  XCircle,
  Clock,
  History,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  Terminal,
  FileText,
  Image,
  Video,
  Search,
  ListChecks,
  BarChart3,
  Cpu,
  Eye,
  X,
  Zap,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";

// ============================================
// 类型定义（对齐规格数据模型）
// ============================================

/** 任务类型 */
type TaskType =
  | "content_generation"
  | "data_query"
  | "task_management"
  | "report_generation";

/** 任务/步骤状态 */
type TaskStatus = "pending" | "running" | "success" | "failed" | "cancelled";

/** 单个执行步骤 */
interface TaskStep {
  id: string;
  /** 步骤名 */
  name: string;
  status: TaskStatus;
  /** 步骤结果 */
  result?: string;
  /** 错误信息 */
  error?: string;
  startedAt?: string;
  completedAt?: string;
}

/** Hermes 执行任务 */
interface HermesTask {
  id: string;
  /** 用户自然语言指令 */
  instruction: string;
  /** 任务类型（自动识别） */
  type: TaskType;
  status: TaskStatus;
  /** 分步执行 */
  steps: TaskStep[];
  /** 最终结果 */
  result?: string;
  /** 目标 Runtime */
  targetRuntime?: string;
  createdAt: string;
  completedAt?: string;
}

// ============================================
// 常量配置
// ============================================

/** localStorage 持久化 key */
const STORAGE_KEY = "xeaos_hermes_tasks";
/** 项目任务本地存储 key（TaskBoard 共用，无 ProjectRuntime 时降级读取） */
const PROJECT_TASKS_KEY = "xeaos_tasks_list";

/** 指令识别规则引擎 */
const INSTRUCTION_RULES: {
  keywords: string[];
  type: TaskType;
  runtime: string;
}[] = [
  { keywords: ["写文章", "生成文章", "公众号", "文案"], type: "content_generation", runtime: "media_studio" },
  { keywords: ["做图", "生成图片", "海报"], type: "content_generation", runtime: "media_studio" },
  { keywords: ["做视频", "生成视频"], type: "content_generation", runtime: "media_studio" },
  { keywords: ["查收入", "今日收入", "财务"], type: "data_query", runtime: "revenue_engine" },
  { keywords: ["查客户", "客户列表", "CRM"], type: "data_query", runtime: "crm" },
  { keywords: ["查项目", "项目进度"], type: "data_query", runtime: "project" },
  { keywords: ["创建任务", "分配任务"], type: "task_management", runtime: "scheduler" },
  { keywords: ["日报", "周报", "分析报告"], type: "report_generation", runtime: "hermes" },
];

/** Runtime 字符串 → RuntimeName 枚举映射（project 无对应 Runtime，单独降级处理） */
const RUNTIME_MAP: Record<string, RuntimeName> = {
  media_studio: RuntimeName.MediaStudio,
  revenue_engine: RuntimeName.RevenueEngine,
  crm: RuntimeName.CRM,
  scheduler: RuntimeName.Scheduler,
  hermes: RuntimeName.Hermes,
};

/** 各 Runtime 的查询参数（真实调用，非假数据） */
const RUNTIME_QUERY: Record<string, Record<string, unknown>> = {
  media_studio: { type: "list_packages", limit: 5 },
  revenue_engine: { type: "today_revenue" },
  crm: { type: "stats" },
  scheduler: { type: "list" },
  hermes: { type: "status" },
};

/** 任务类型元信息（标签 + 图标 + 主色） */
const TASK_TYPE_META: Record<
  TaskType,
  { label: string; icon: typeof FileText; color: string }
> = {
  content_generation: { label: "内容生成", icon: FileText, color: "#A855F7" },
  data_query: { label: "数据查询", icon: Search, color: "#60A5FA" },
  task_management: { label: "任务管理", icon: ListChecks, color: "#34D399" },
  report_generation: { label: "报告生成", icon: BarChart3, color: "#FBBF24" },
};

/** 状态元信息（标签 + 图标 + 主色） */
const STATUS_META: Record<
  TaskStatus,
  { label: string; icon: typeof Clock; color: string }
> = {
  pending: { label: "待执行", icon: Clock, color: "#6B7280" },
  running: { label: "执行中", icon: Loader2, color: "#A855F7" },
  success: { label: "成功", icon: CheckCircle2, color: "#10B981" },
  failed: { label: "失败", icon: AlertOctagon, color: "#EF4444" },
  cancelled: { label: "已取消", icon: XCircle, color: "#6B7280" },
};

/** 指令快捷示例（覆盖 5+ 功能） */
const QUICK_INSTRUCTIONS: { label: string; instruction: string; icon: typeof Zap }[] = [
  { label: "生成公众号文章", instruction: "帮我生成一篇公众号文章", icon: FileText },
  { label: "做图", instruction: "帮我做图", icon: Image },
  { label: "做视频", instruction: "帮我做视频", icon: Video },
  { label: "查今日收入", instruction: "查今日收入", icon: BarChart3 },
  { label: "查客户列表", instruction: "查客户列表", icon: Search },
  { label: "查项目进度", instruction: "查项目进度", icon: ListChecks },
  { label: "创建任务", instruction: "创建任务", icon: ListChecks },
  { label: "生成日报", instruction: "生成日报", icon: BarChart3 },
];

// ============================================
// localStorage 持久化
// ============================================

/** 加载历史任务列表 */
function loadTasks(): HermesTask[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as HermesTask[];
    if (!Array.isArray(list)) return [];
    return list;
  } catch {
    return [];
  }
}

/** 保存任务列表 */
function saveTasks(tasks: HermesTask[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
  } catch {
    // 静默降级
  }
}

/** 读取本地项目任务（无 ProjectRuntime 时降级数据源） */
function loadProjectTasks(): unknown[] {
  try {
    const raw = localStorage.getItem(PROJECT_TASKS_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
function genId(): string {
  return `ht_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** 指令识别：根据关键词命中返回任务类型与目标 Runtime */
function recognizeInstruction(
  instruction: string
): { type: TaskType; runtime: string } {
  const text = instruction.trim();
  for (const rule of INSTRUCTION_RULES) {
    if (rule.keywords.some((kw) => text.includes(kw))) {
      return { type: rule.type, runtime: rule.runtime };
    }
  }
  // 未命中规则 → 兜底交由 Hermes 处理
  return { type: "report_generation", runtime: "hermes" };
}

/** 根据任务类型构建分步执行计划（复杂任务分解） */
function buildStepPlan(type: TaskType, runtime: string): { name: string; kind: "parse" | "call" | "format" }[] {
  switch (type) {
    case "content_generation":
      return [
        { name: "解析创作指令", kind: "parse" },
        { name: `调用 ${runtime} 获取内容包`, kind: "call" },
        { name: "整理可用素材", kind: "format" },
      ];
    case "data_query":
      return [
        { name: "解析查询意图", kind: "parse" },
        { name: `调用 ${runtime} 查询数据`, kind: "call" },
        { name: "聚合查询结果", kind: "format" },
      ];
    case "task_management":
      return [
        { name: "解析管理指令", kind: "parse" },
        { name: `调用 ${runtime} 读取任务`, kind: "call" },
        { name: "输出任务状态", kind: "format" },
      ];
    case "report_generation":
      return [
        { name: "解析报告需求", kind: "parse" },
        { name: "收集数据源", kind: "call" },
        { name: `调用 ${runtime} 生成报告`, kind: "format" },
      ];
    default:
      return [
        { name: "解析指令", kind: "parse" },
        { name: `调用 ${runtime}`, kind: "call" },
      ];
  }
}

/** 等待 RuntimeManager 初始化完成（最多 6 秒） */
async function awaitRuntimeReady(): Promise<boolean> {
  for (let i = 0; i < 20; i++) {
    if (RuntimeManager.isInitialized()) return true;
    await new Promise((r) => window.setTimeout(r, 300));
  }
  return RuntimeManager.isInitialized();
}

/**
 * 执行跨模块调用
 * - 已注册 Runtime → RuntimeManager.query
 * - project 无 Runtime → 降级读取 localStorage（xeaos_tasks_list）
 */
async function callRuntime(
  runtime: string
): Promise<{ success: boolean; data?: unknown; error?: string }> {
  // project 无对应 Runtime，降级读取本地项目任务
  if (runtime === "project") {
    const list = loadProjectTasks();
    return { success: true, data: { source: "localStorage", count: list.length, items: list } };
  }

  const runtimeName = RUNTIME_MAP[runtime];
  if (!runtimeName) {
    return { success: false, error: `未知的 Runtime: ${runtime}` };
  }

  const ready = await awaitRuntimeReady();
  if (!ready) {
    return { success: false, error: "RuntimeManager 尚未初始化" };
  }

  try {
    const resp = await RuntimeManager.query(runtimeName, RUNTIME_QUERY[runtime]);
    if (resp?.success) {
      return { success: true, data: resp.data };
    }
    return { success: false, error: resp?.error || `${runtime} 查询失败` };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    return { success: false, error: msg };
  }
}

/** 将 Runtime 返回数据格式化为可读摘要（禁止假数据，全部基于真实返回） */
function formatRuntimeResult(runtime: string, data: unknown): string {
  if (data == null) return "（无数据返回）";

  // project 降级路径
  if (runtime === "project") {
    const d = data as { count?: number; items?: unknown[] };
    const count = d?.count ?? 0;
    const items = Array.isArray(d?.items) ? d.items! : [];
    const done = items.filter((t: any) => t?.status === "done").length;
    const inProgress = items.filter((t: any) => t?.status === "in_progress").length;
    return `本地项目任务共 ${count} 个（已完成 ${done} / 进行中 ${inProgress}）。`;
  }

  // 通用字段提取
  const obj = data as Record<string, any>;

  // RevenueEngine: today_revenue
  if (runtime === "revenue_engine") {
    const total = obj?.total ?? obj?.amount ?? obj?.revenue;
    const source = obj?.dataSource ?? obj?.source ?? "real";
    if (typeof total === "number") {
      return `今日收入：¥${total.toLocaleString("zh-CN")}（数据来源：${source}）。`;
    }
    return `收入数据已获取（数据来源：${source}）。`;
  }

  // CRM: stats
  if (runtime === "crm") {
    const total = obj?.total ?? obj?.count ?? obj?.customerCount;
    if (typeof total === "number") {
      return `CRM 客户共 ${total} 个。`;
    }
    return "CRM 统计数据已获取。";
  }

  // MediaStudio: list_packages
  if (runtime === "media_studio") {
    const list: any[] = Array.isArray(data)
      ? data
      : obj?.packages ?? obj?.data ?? [];
    const count = list.length;
    if (count === 0) return "媒体工作室暂无内容包，可在媒体工作室中生成。";
    const titles = list.slice(0, 3).map((p: any) => String(p?.topic ?? p?.title ?? p?.filename ?? "未命名"));
    return `媒体工作室共 ${count} 个内容包，最近：${titles.join("、")}。`;
  }

  // Scheduler: list
  if (runtime === "scheduler") {
    const list: any[] = Array.isArray(data)
      ? data
      : obj?.tasks ?? obj?.data ?? [];
    const enabled = list.filter((t: any) => t?.enabled !== false).length;
    return `调度器共 ${list.length} 个任务（启用 ${enabled} 个）。`;
  }

  // Hermes: status
  if (runtime === "hermes") {
    const initialized = obj?.initialized;
    const provider = obj?.aiProvider ?? "未配置";
    return `Hermes 状态：${initialized ? "已就绪" : "未就绪"}，AI 提供方：${provider}。`;
  }

  // 兜底：JSON 摘要
  try {
    const json = JSON.stringify(data);
    return json.length > 200 ? json.slice(0, 200) + "..." : json;
  } catch {
    return String(data);
  }
}

/** 计算任务进度百分比 */
function calcProgress(task: HermesTask): number {
  if (task.steps.length === 0) return 0;
  const done = task.steps.filter((s) => s.status === "success" || s.status === "failed" || s.status === "cancelled").length;
  return Math.round((done / task.steps.length) * 100);
}

// ============================================
// 子组件：指令输入区
// ============================================

interface InstructionInputProps {
  onExecute: (instruction: string) => void;
  running: boolean;
}

function InstructionInput({ onExecute, running }: InstructionInputProps) {
  const [value, setValue] = useState("");
  const recognized = useMemo(() => (value.trim() ? recognizeInstruction(value) : null), [value]);

  const handleSubmit = () => {
    const text = value.trim();
    if (!text || running) return;
    onExecute(text);
    setValue("");
  };

  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-3">
      {/* 输入框 + 执行按钮 */}
      <div className="flex items-center gap-2">
        <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border border-white/[0.06] bg-white/[0.02] focus-within:border-purple-500/40 transition-colors">
          <Terminal size={13} className="text-purple-400 flex-shrink-0" />
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder="输入自然语言指令，例如：帮我生成一篇公众号文章"
            disabled={running}
            className="flex-1 bg-transparent text-[13px] text-white placeholder:text-gray-600 outline-none disabled:opacity-50"
          />
          {/* 实时识别的任务类型标签 */}
          {recognized && (
            <motion.span
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono"
              style={{
                color: TASK_TYPE_META[recognized.type].color,
                background: `${TASK_TYPE_META[recognized.type].color}1a`,
                border: `1px solid ${TASK_TYPE_META[recognized.type].color}33`,
              }}
            >
              <Cpu size={9} />
              {TASK_TYPE_META[recognized.type].label} · {recognized.runtime}
            </motion.span>
          )}
        </div>
        <button
          onClick={handleSubmit}
          disabled={!value.trim() || running}
          title="执行指令"
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-purple-500/30 bg-purple-500/10 hover:bg-purple-500/20 text-purple-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {running ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <Send size={13} />
          )}
          <span className="text-[12px] font-medium">{running ? "执行中" : "执行"}</span>
        </button>
      </div>

      {/* 快捷指令 */}
      <div className="flex flex-wrap gap-1.5 mt-2.5">
        {QUICK_INSTRUCTIONS.map((q) => {
          const Icon = q.icon;
          return (
            <button
              key={q.label}
              onClick={() => !running && onExecute(q.instruction)}
              disabled={running}
              className="flex items-center gap-1 px-2 py-1 rounded-md border border-white/[0.05] bg-white/[0.01] hover:border-purple-500/30 hover:bg-purple-500/[0.06] text-gray-400 hover:text-purple-300 transition-colors disabled:opacity-40"
            >
              <Icon size={10} />
              <span className="text-[11px]">{q.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// 子组件：单步执行项
// ============================================

function StepItem({ step, index }: { step: TaskStep; index: number }) {
  const meta = STATUS_META[step.status];
  const Icon = meta.icon;
  const isRunning = step.status === "running";

  return (
    <div className="flex items-start gap-2.5 py-1.5">
      {/* 序号 + 状态图标 */}
      <div className="flex-shrink-0 mt-0.5">
        <div
          className="w-5 h-5 rounded-md flex items-center justify-center"
          style={{
            background: `${meta.color}1a`,
            border: `1px solid ${meta.color}33`,
          }}
        >
          <Icon
            size={11}
            style={{ color: meta.color }}
            className={isRunning ? "animate-spin" : ""}
          />
        </div>
      </div>
      {/* 步骤内容 */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono text-gray-600">{String(index + 1).padStart(2, "0")}</span>
          <span className="text-[12px] text-gray-200">{step.name}</span>
          <span
            className="text-[9px] font-mono px-1.5 py-0.5 rounded"
            style={{ color: meta.color, background: `${meta.color}14` }}
          >
            {meta.label}
          </span>
        </div>
        {/* 步骤结果 / 错误 */}
        {step.result && (
          <div className="mt-1 text-[11px] text-gray-400 leading-relaxed break-all line-clamp-3">
            {step.result}
          </div>
        )}
        {step.error && (
          <div className="mt-1 text-[11px] text-red-400/90 leading-relaxed break-all">
            {step.error}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// 子组件：当前执行任务卡片
// ============================================

interface CurrentTaskCardProps {
  task: HermesTask | null;
  onRetry: () => void;
  onCancel: () => void;
  onPreview: (task: HermesTask) => void;
}

function CurrentTaskCard({ task, onRetry, onCancel, onPreview }: CurrentTaskCardProps) {
  if (!task) {
    return (
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-4 flex flex-col items-center justify-center text-center min-h-[120px]">
        <Sparkles size={18} className="text-purple-500/40 mb-2" />
        <p className="text-[12px] text-gray-500">暂无执行任务</p>
        <p className="text-[11px] text-gray-600 mt-0.5">输入指令开始</p>
      </div>
    );
  }

  const typeMeta = TASK_TYPE_META[task.type];
  const statusMeta = STATUS_META[task.status];
  const TypeIcon = typeMeta.icon;
  const StatusIcon = statusMeta.icon;
  const progress = calcProgress(task);
  const isRunning = task.status === "running";
  const isFailed = task.status === "failed";

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden"
    >
      {/* 头部：指令 + 类型 + 状态 */}
      <div className="px-3 py-2.5 border-b border-white/[0.06]">
        <div className="flex items-center gap-2 mb-1.5">
          <TypeIcon size={13} style={{ color: typeMeta.color }} />
          <span
            className="text-[10px] font-mono px-1.5 py-0.5 rounded"
            style={{ color: typeMeta.color, background: `${typeMeta.color}14` }}
          >
            {typeMeta.label}
          </span>
          {task.targetRuntime && (
            <span className="text-[10px] font-mono text-gray-500">→ {task.targetRuntime}</span>
          )}
          <div className="ml-auto flex items-center gap-1.5">
            <StatusIcon
              size={12}
              style={{ color: statusMeta.color }}
              className={isRunning ? "animate-spin" : ""}
            />
            <span
              className="text-[10px] font-mono"
              style={{ color: statusMeta.color }}
            >
              {statusMeta.label}
            </span>
          </div>
        </div>
        {/* 用户指令 */}
        <div className="text-[13px] text-white leading-snug line-clamp-2">
          <span className="text-purple-400 mr-1">›</span>
          {task.instruction}
        </div>
      </div>

      {/* 进度条 */}
      <div className="px-3 py-2 border-b border-white/[0.06]">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
            执行进度
          </span>
          <span className="text-[11px] font-mono text-purple-300">{progress}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{
              background: isFailed
                ? "linear-gradient(90deg, #EF4444, #F87171)"
                : "linear-gradient(90deg, #A855F7, #C084FC)",
            }}
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          />
        </div>
      </div>

      {/* 分步执行 */}
      <div className="px-3 py-2 max-h-[200px] overflow-y-auto">
        {task.steps.map((step, i) => (
          <StepItem key={step.id} step={step} index={i} />
        ))}
      </div>

      {/* 操作栏 */}
      <div className="flex items-center gap-2 px-3 py-2 border-t border-white/[0.06] bg-white/[0.01]">
        {task.result && (
          <button
            onClick={() => onPreview(task)}
            className="flex items-center gap-1 px-2 py-1 rounded-md border border-purple-500/30 bg-purple-500/10 hover:bg-purple-500/20 text-purple-300 transition-colors"
          >
            <Eye size={11} />
            <span className="text-[11px]">查看结果</span>
          </button>
        )}
        {isFailed && (
          <button
            onClick={onRetry}
            className="flex items-center gap-1 px-2 py-1 rounded-md border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 transition-colors"
          >
            <RotateCcw size={11} />
            <span className="text-[11px]">重试</span>
          </button>
        )}
        {isRunning && (
          <button
            onClick={onCancel}
            className="flex items-center gap-1 px-2 py-1 rounded-md border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-300 transition-colors"
          >
            <XCircle size={11} />
            <span className="text-[11px]">取消</span>
          </button>
        )}
        <span className="ml-auto text-[10px] font-mono text-gray-600">
          {new Date(task.createdAt).toLocaleTimeString("zh-CN")}
        </span>
      </div>
    </motion.div>
  );
}

// ============================================
// 子组件：任务历史列表
// ============================================

interface TaskHistoryListProps {
  tasks: HermesTask[];
  onPreview: (task: HermesTask) => void;
  onClear: () => void;
}

function TaskHistoryList({ tasks, onPreview, onClear }: TaskHistoryListProps) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="flex-1 min-h-0 flex flex-col rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden">
      {/* 头部 */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.06]">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-2 hover:text-white transition-colors"
        >
          {expanded ? (
            <ChevronDown size={12} className="text-gray-400" />
          ) : (
            <ChevronRight size={12} className="text-gray-400" />
          )}
          <History size={12} className="text-purple-400" />
          <span className="text-[12px] font-medium text-white">任务历史</span>
          <span className="text-[10px] font-mono text-gray-500">({tasks.length})</span>
        </button>
        {tasks.length > 0 && (
          <button
            onClick={onClear}
            className="text-[10px] font-mono text-gray-500 hover:text-red-400 transition-colors"
            title="清空历史"
          >
            清空
          </button>
        )}
      </div>

      {/* 列表 */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="flex-1 min-h-0 overflow-y-auto"
          >
            {tasks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <History size={16} className="text-gray-700 mb-2" />
                <p className="text-[11px] text-gray-600">暂无执行任务</p>
                <p className="text-[10px] text-gray-700 mt-0.5">输入指令开始</p>
              </div>
            ) : (
              <div className="p-2 space-y-1.5">
                {tasks.map((task) => {
                  const typeMeta = TASK_TYPE_META[task.type];
                  const statusMeta = STATUS_META[task.status];
                  const TypeIcon = typeMeta.icon;
                  const StatusIcon = statusMeta.icon;
                  return (
                    <button
                      key={task.id}
                      onClick={() => onPreview(task)}
                      className="w-full text-left rounded-lg border border-white/[0.04] bg-white/[0.01] px-2.5 py-2 hover:border-purple-500/30 hover:bg-purple-500/[0.03] transition-colors group"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <TypeIcon size={11} style={{ color: typeMeta.color }} />
                        <span
                          className="text-[9px] font-mono px-1 py-0.5 rounded"
                          style={{ color: typeMeta.color, background: `${typeMeta.color}14` }}
                        >
                          {typeMeta.label}
                        </span>
                        <StatusIcon
                          size={10}
                          style={{ color: statusMeta.color }}
                          className="ml-auto"
                        />
                        <span
                          className="text-[9px] font-mono"
                          style={{ color: statusMeta.color }}
                        >
                          {statusMeta.label}
                        </span>
                      </div>
                      <div className="text-[12px] text-gray-200 leading-snug line-clamp-1">
                        {task.instruction}
                      </div>
                      {task.result && (
                        <div className="text-[10px] text-gray-500 leading-snug line-clamp-1 mt-0.5">
                          {task.result}
                        </div>
                      )}
                      <div className="text-[9px] font-mono text-gray-600 mt-1">
                        {new Date(task.createdAt).toLocaleString("zh-CN", {
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// 子组件：结果预览 Modal
// ============================================

function ResultPreviewModal({
  task,
  onClose,
}: {
  task: HermesTask | null;
  onClose: () => void;
}) {
  return (
    <AnimatePresence>
      {task && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-8"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, y: 10 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 10 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-2xl max-h-[80vh] flex flex-col rounded-xl border border-white/10 bg-[#0a0e1a] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
              <div className="flex items-center gap-2">
                <Eye size={14} className="text-purple-400" />
                <h3 className="text-[14px] font-semibold text-white">执行结果</h3>
                {task.targetRuntime && (
                  <span className="text-[11px] font-mono text-gray-600 ml-2 px-1.5 py-0.5 rounded bg-white/[0.04]">
                    {task.targetRuntime}
                  </span>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05] transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-4">
              {/* 指令 */}
              <div>
                <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
                  指令
                </div>
                <div className="text-[13px] text-white bg-white/[0.02] border border-white/[0.06] rounded-lg px-3 py-2">
                  {task.instruction}
                </div>
              </div>

              {/* 分步详情 */}
              <div>
                <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
                  分步执行
                </div>
                <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg px-3 py-2 space-y-2">
                  {task.steps.map((step, i) => (
                    <StepItem key={step.id} step={step} index={i} />
                  ))}
                </div>
              </div>

              {/* 最终结果 */}
              <div>
                <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
                  最终结果
                </div>
                <div className="text-[12px] text-gray-200 bg-purple-500/[0.04] border border-purple-500/20 rounded-lg px-3 py-2.5 leading-relaxed whitespace-pre-wrap break-all">
                  {task.result || "（无结果）"}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-5 py-2 border-t border-white/[0.06] bg-white/[0.01]">
              <span className="text-[11px] font-mono text-gray-600">
                创建：{new Date(task.createdAt).toLocaleString("zh-CN")}
              </span>
              {task.completedAt && (
                <span className="text-[11px] font-mono text-gray-600">
                  完成：{new Date(task.completedAt).toLocaleString("zh-CN")}
                </span>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ============================================
// 主组件：HermesTaskExecutor
// ============================================

export default function HermesTaskExecutor() {
  const [tasks, setTasks] = useState<HermesTask[]>([]);
  const [currentTask, setCurrentTask] = useState<HermesTask | null>(null);
  const [running, setRunning] = useState(false);
  const [previewTask, setPreviewTask] = useState<HermesTask | null>(null);

  /** 取消信号（用于中止正在执行的任务） */
  const cancelRef = useRef(false);

  // 初始化：加载历史任务
  useEffect(() => {
    const list = loadTasks();
    setTasks(list);
  }, []);

  /** 持久化任务列表 */
  const persist = useCallback((next: HermesTask[]) => {
    setTasks(next);
    saveTasks(next);
  }, []);

  /** 更新当前任务并同步到历史列表 */
  const syncCurrent = useCallback(
    (updated: HermesTask) => {
      setCurrentTask(updated);
      setTasks((prev) => {
        const idx = prev.findIndex((t) => t.id === updated.id);
        if (idx === -1) {
          const next = [updated, ...prev];
          saveTasks(next);
          return next;
        }
        const next = [...prev];
        next[idx] = updated;
        saveTasks(next);
        return next;
      });
    },
    []
  );

  /** 执行单个步骤，返回结果或抛错 */
  const executeStep = useCallback(
    async (step: TaskStep, task: HermesTask): Promise<string> => {
      // 依据步骤名判断调用类型
      if (step.name.includes("调用")) {
        const result = await callRuntime(task.targetRuntime!);
        if (!result.success) {
          throw new Error(result.error || "调用失败");
        }
        return formatRuntimeResult(task.targetRuntime!, result.data);
      }
      // 解析/格式化步骤：本地处理
      if (step.name.includes("解析")) {
        return `已识别任务类型：${TASK_TYPE_META[task.type].label}，目标：${task.targetRuntime}`;
      }
      // 格式化 / 整理 / 聚合 / 输出步骤：基于已完成的调用步骤结果
      const prevCallStep = task.steps.find((s) => s.name.includes("调用") && s.result);
      if (prevCallStep?.result) {
        return prevCallStep.result;
      }
      return "步骤完成";
    },
    []
  );

  /** 执行任务（分步推进，支持从失败步骤重试） */
  const runTask = useCallback(
    async (task: HermesTask, fromStepIndex: number = 0) => {
      cancelRef.current = false;
      setRunning(true);

      let working: HermesTask = {
        ...task,
        status: "running",
        steps: task.steps.map((s, i) =>
          i < fromStepIndex
            ? s
            : { ...s, status: "pending", result: undefined, error: undefined, startedAt: undefined, completedAt: undefined }
        ),
      };
      syncCurrent(working);

      try {
        for (let i = fromStepIndex; i < working.steps.length; i++) {
          if (cancelRef.current) {
            // 用户取消
            const cancelledStep: TaskStep = {
              ...working.steps[i],
              status: "cancelled",
              completedAt: new Date().toISOString(),
            };
            working = {
              ...working,
              steps: working.steps.map((s, idx) => (idx === i ? cancelledStep : s)),
              status: "cancelled",
              completedAt: new Date().toISOString(),
            };
            syncCurrent(working);
            setRunning(false);
            return;
          }

          // 标记当前步骤为 running
          const runningStep: TaskStep = {
            ...working.steps[i],
            status: "running",
            startedAt: new Date().toISOString(),
          };
          working = {
            ...working,
            steps: working.steps.map((s, idx) => (idx === i ? runningStep : s)),
          };
          syncCurrent(working);

          // 模拟步骤间可见延迟（让进度反馈可感知）
          await new Promise((r) => window.setTimeout(r, 300));

          if (cancelRef.current) {
            working = {
              ...working,
              steps: working.steps.map((s, idx) =>
                idx === i ? { ...s, status: "cancelled", completedAt: new Date().toISOString() } : s
              ),
              status: "cancelled",
              completedAt: new Date().toISOString(),
            };
            syncCurrent(working);
            setRunning(false);
            return;
          }

          try {
            const result = await executeStep(working.steps[i], working);
            const doneStep: TaskStep = {
              ...working.steps[i],
              status: "success",
              result,
              completedAt: new Date().toISOString(),
            };
            working = {
              ...working,
              steps: working.steps.map((s, idx) => (idx === i ? doneStep : s)),
            };
            syncCurrent(working);
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            const failedStep: TaskStep = {
              ...working.steps[i],
              status: "failed",
              error: msg,
              completedAt: new Date().toISOString(),
            };
            // 后续未执行步骤标记为 cancelled
            working = {
              ...working,
              steps: working.steps.map((s, idx) => {
                if (idx === i) return failedStep;
                if (idx > i) return { ...s, status: "cancelled" };
                return s;
              }),
              status: "failed",
              completedAt: new Date().toISOString(),
              result: `执行失败：${msg}`,
            };
            syncCurrent(working);
            setRunning(false);
            return;
          }
        }

        // 全部步骤完成 → 汇总最终结果
        const callStep = working.steps.find((s) => s.name.includes("调用") && s.result);
        const finalResult =
          callStep?.result ||
          working.steps[working.steps.length - 1]?.result ||
          "任务执行完成";
        working = {
          ...working,
          status: "success",
          result: finalResult,
          completedAt: new Date().toISOString(),
        };
        syncCurrent(working);
      } finally {
        setRunning(false);
      }
    },
    [executeStep, syncCurrent]
  );

  /** 提交新指令 */
  const handleExecute = useCallback(
    (instruction: string) => {
      if (running) return;
      const { type, runtime } = recognizeInstruction(instruction);
      const plan = buildStepPlan(type, runtime);
      const task: HermesTask = {
        id: genId(),
        instruction,
        type,
        status: "pending",
        steps: plan.map((p) => ({
          id: genId(),
          name: p.name,
          status: "pending" as TaskStatus,
        })),
        targetRuntime: runtime,
        createdAt: new Date().toISOString(),
      };
      setCurrentTask(task);
      // 立即写入历史
      setTasks((prev) => {
        const next = [task, ...prev];
        saveTasks(next);
        return next;
      });
      void runTask(task, 0);
    },
    [running, runTask]
  );

  /** 重试当前失败任务（从失败步骤开始） */
  const handleRetry = useCallback(() => {
    if (!currentTask || running) return;
    const failedIdx = currentTask.steps.findIndex((s) => s.status === "failed");
    const startIdx = failedIdx >= 0 ? failedIdx : 0;
    void runTask(currentTask, startIdx);
  }, [currentTask, running, runTask]);

  /** 取消正在执行的任务 */
  const handleCancel = useCallback(() => {
    cancelRef.current = true;
  }, []);

  /** 清空历史 */
  const handleClear = useCallback(() => {
    persist([]);
    if (currentTask && !running) setCurrentTask(null);
  }, [persist, currentTask, running]);

  return (
    <div className="h-full w-full flex flex-col gap-3 overflow-hidden">
      {/* ============ 顶部：指令输入 ============ */}
      <InstructionInput onExecute={handleExecute} running={running} />

      {/* ============ 中部：当前执行任务 ============ */}
      <CurrentTaskCard
        task={currentTask}
        onRetry={handleRetry}
        onCancel={handleCancel}
        onPreview={setPreviewTask}
      />

      {/* ============ 底部：任务历史 ============ */}
      <TaskHistoryList
        tasks={tasks}
        onPreview={setPreviewTask}
        onClear={handleClear}
      />

      {/* ============ 结果预览 Modal ============ */}
      <ResultPreviewModal task={previewTask} onClose={() => setPreviewTask(null)} />
    </div>
  );
}
